import React from 'react';
import styled from 'styled-components';
import { semantic, space, typeRole } from '@repo/design-tokens';
import { Banner, Button } from '@repo/design-system';
import type { RTSPEndpoint, StatementObserver } from '@repo/frontend-types';

import type { MatchObserversResult } from './matchObserversToPlan';
import { unplacedCompositeSentence } from './matchCompositesToPlan';
import type { MatchCompositesResult } from './matchCompositesToPlan';
import type { EntityNamer } from './entityCodeLabel';
import { trackSideLabel } from './observerSessionState';
import type {
  ConfigOutcome,
  ModelOutcome,
  ObserverSource,
  ReadOutcome,
} from './useLiveTwinConfiguration';

/**
 * What the Observers stage says about the twin's live configuration — including when it could not
 * read it.
 *
 * ## Why this is a component rather than three lines inside the stage
 *
 * The failure this whole feature exists to remove is a screen that renders **a broken read and an
 * empty gym identically**. An operator whose configuration read failed saw the same zeros as an
 * operator whose twin genuinely measures nothing, and the next thing either of them does is press
 * Go Live. So the distinctness of these sentences is the deliverable, not decoration — and it is
 * pinned by `LiveConfigurationNotices.test.tsx`, which asserts that the loading, empty and errored
 * renderings of the same read share no sentence.
 *
 * It lives in its own file because `OnboardingObservers.tsx` imports the planner canvas, which
 * pulls the 3D plan into jsdom; a presentational component with no canvas can be rendered by React
 * Testing Library in a few milliseconds, and that is what makes the assertions above affordable.
 *
 * ## Design-system notes
 *
 * `Banner` from `@repo/design-system` (card 09, "Inline banner"), `surface="on-solid"` on every one
 * of them — the wizard stage is a white panel, and `Banner`'s own header is explicit that an
 * `on-glass` banner on white reads as an unbordered floating paragraph. Same posture as the
 * Equipment step's uploader banners (`AssetCsvUploader.tsx`), for the same reason. No `icon` is passed: `Banner`
 * renders no disc without one, and importing a glyph here would raise the count of files pulling
 * `@fluentui/react-icons`, which is ratchet-only.
 *
 * Tone is carried by colour and a decorative glyph only, so **every sentence below states the
 * severity in words** (WCAG 1.4.1) — "Could not read this twin's observers.", never a bare count
 * tinted red. AC16 turns that into a criterion for the unmatched count specifically.
 */

export interface LiveConfigurationNoticesProps {
  className?: string;
  streams: ReadOutcome<RTSPEndpoint[]>;
  config: ConfigOutcome;
  observers: ReadOutcome<StatementObserver[]>;
  /**
   * The running-model read (`camera-observer-setup-attention`). Its failure is said here and the
   * *Observer needs checking* group renders as unknown; its `none` is not a notice at all — a twin
   * that has never gone live has nothing to compare against, and that is not a failure. Optional so
   * a host that does not read the model renders exactly as before.
   */
  model?: ModelOutcome;
  /** The last match's counts. `null` until one has run. */
  match: MatchObserversResult | null;
  /**
   * The last COMPLEX MEASURE match's counts. `null` until one has run, and optional so a host that
   * does not load composites renders exactly as before.
   */
  compositeMatch?: MatchCompositesResult | null;
  /** Re-runs one read. Each banner's Retry calls its own, never all of them (FR13). */
  onRetry: (which: 'streams' | 'config' | 'observers' | 'model') => void;
  /**
   * Which observers the plan holds after the match (`observers-page-draft`, FR10). On `'draft'` the
   * observer sentence says the plan's saved draft is on screen, not the running set. Optional, so a
   * host that does not say renders exactly as before.
   */
  observerSource?: ObserverSource | null;
  /** The plan's own observer count across its current designs — `{N}` in the draft sentence. */
  draftObserverCount?: number;
  /**
   * Names an entity code as *Name (CODE)* (`onboarding-polish-observer-notices`, R1–R3), from the
   * plan, the asset register, the running model and the observer's stored name, in that order.
   * Optional: without it every code is shown as it is, exactly as before.
   */
  nameFor?: EntityNamer;
}

/** How many unplaceable identifiers are named before the rest become a count. */
const NAMED_IDENTIFIER_LIMIT = 3;

const plural = (n: number, singular: string): string =>
  `${n} ${n === 1 ? singular : `${singular}s`}`;

/**
 * A Retry named for what it retries.
 *
 * AC15: three buttons that would otherwise all be called "Retry", on a stage where the difference
 * between retrying the stream read and retrying the observer read is the difference between a
 * nameless camera and no observers at all. The visible word stays "Retry" — it is the accessible
 * name that carries the distinction, and it opens with the visible label so the two agree
 * (WCAG 2.5.3).
 */
const RetryButton: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <Button type="button" variant="ghost" size="xs" aria-label={label} onClick={onClick}>
    Retry
  </Button>
);

/*
 * ⚠ **These banners announce through their OWN roles, and an earlier version of this file silenced
 * them.**
 *
 * AC16 asks for the three failures to announce from one `aria-live="assertive"` region. That was
 * built as a wrapper region with `aria-live="off"` on each banner inside it, to avoid nesting one
 * live region in another. It cannot work: politeness resolves from the nearest ancestor-or-self
 * carrying an explicit `aria-live`, and `off` on the inserted node itself means the insertion is
 * not presented AT ALL — it does not defer outward. The arrangement that was meant to announce
 * once announced never, for exactly the operator this feature was built to stop misleading.
 *
 * `Banner` already resolves this correctly on its own: `role="alert"` for the error tone
 * (assertive) and `role="status"` for the rest (polite). So the wrappers are plain grouping
 * elements now, and each banner speaks for itself. The criterion's intent — every failure
 * announced, errors interrupting, advisories not — is met; the mechanism it names is not, and the
 * spec records that correction rather than this file quietly diverging from it.
 */

const LiveConfigurationNotices: React.FC<LiveConfigurationNoticesProps> = ({
  className,
  streams,
  config,
  observers,
  model,
  match,
  compositeMatch,
  onRetry,
  observerSource,
  draftObserverCount = 0,
  nameFor,
}) => {
  const streamsEmpty = streams.status === 'ready' && streams.value.length === 0;
  const modelFailed = model?.status === 'failed';
  /* Entities the running model holds with no identity were not compared (Failure States, partial):
     said as information, only when there are any, and only beside a model that was read. Phrased
     here, not by the converter — its sentence is written for the chooser's IMPORT, and nothing is
     imported on this stage. */
  const modelSkipped = model?.status === 'ready' && model.skipped > 0 ? model.skipped : 0;
  const configurationEmpty =
    config.status === 'ready' && observers.status === 'ready' && observers.value.length === 0;
  const ambiguous = config.status === 'ready' && config.activeIds.length > 1;
  /*
   * NOTHING is flagged active, so the rule fell back to the most recent configuration.
   *
   * `resolveActiveConfigId` returns `activeIds: []` for exactly this and its header says a caller
   * rendering nothing about it "is choosing to, rather than being unable to". FR2 asks for the
   * ACTIVE configuration; showing a deactivated twin's last one as though it were live, with
   * nothing to distinguish it, is the same false claim as rendering a failure as an absence.
   */
  const noneActive = config.status === 'ready' && config.activeIds.length === 0;

  /*
   * What the running set's observers could not be taken as. Withheld when the plan's draft is on
   * screen instead (FR10): those observers were not loaded, so their problems are not this screen's.
   */
  const runningShown = match && observerSource !== 'draft' ? match : null;
  const unplaceable = runningShown ? runningShown.unmatched.length : 0;
  const unreadable = runningShown ? runningShown.unreadableRegions.length : 0;
  const unrecognised = runningShown ? runningShown.unrecognisedTrackSides.length : 0;
  const anythingToReport =
    unplaceable + unreadable + unrecognised + (compositeMatch?.unplaced.length ?? 0) > 0;

  /*
   * AC16's sentence, verbatim: "150 observers loaded. 1 could not be placed." The count is in the
   * text and not only in the banner's colour, which is the criterion — an operator who cannot
   * distinguish amber from green still learns that one observer did not make it onto the plan.
   *
   * Withheld once the observer read is no longer `ready`. A Retry that succeeded and then failed
   * leaves the last match standing, and a count from the previous attempt rendered beside "could
   * not read this twin's observers" is the same contradiction this component exists to remove,
   * inverted.
   */
  /*
   * The complex measures the plan just took on — one sentence appended to the observer summary,
   * under the same rules: the count is in the TEXT and not only in the banner's colour, and a
   * measure that could not be placed is said rather than quietly dropped.
   *
   * Withheld with the rest of the summary once the read is no longer `ready`, for the same reason:
   * a count from a previous attempt sitting beside "could not read this twin's configuration
   * statement" is the contradiction this component exists to remove, inverted.
   */
  const compositesUnplaced = compositeMatch ? compositeMatch.unplaced.length : 0;
  const compositeSummary = compositeMatch
    ? ` ${plural(compositeMatch.placed, 'composite measure')} loaded.` +
      (compositesUnplaced > 0 ? ` ${compositesUnplaced} could not be placed.` : '')
    : '';

  /*
   * FR10: when the plan's saved draft won over the running configuration, *N observers loaded*
   * would claim the running set is on screen. It is not, so the sentence says which is — and the
   * *could not be placed* clause goes, because it describes the running set.
   */
  const loadSummary =
    match && observers.status === 'ready' && config.status === 'ready'
      ? observerSource === 'draft'
        ? `Showing your saved draft: ${plural(draftObserverCount, 'observer')}, not yet launched. ` +
          // The STATEMENT's count — what the twin runs — not how many of it this plan could place.
          `The twin is running ${observers.value.length}.` +
          compositeSummary
        : `${plural(match.matched, 'observer')} loaded.` +
          (unplaceable > 0 ? ` ${unplaceable} could not be placed.` : '') +
          compositeSummary
      : null;

  const named = runningShown
    ? runningShown.unmatched
        .slice(0, NAMED_IDENTIFIER_LIMIT)
        .map((code, i) => (nameFor ? nameFor(code, runningShown.unmatchedNames[i] ?? null) : code))
    : [];
  const beyondNamed = unplaceable - named.length;
  const unrecognisedValues = runningShown
    ? Array.from(new Set(runningShown.unrecognisedTrackSides.map(u => u.value)))
    : [];
  const namedComposites = compositeMatch
    ? compositeMatch.unplaced.slice(0, NAMED_IDENTIFIER_LIMIT)
    : [];
  const beyondNamedComposites = compositesUnplaced - namedComposites.length;

  /*
   * Whether anything is actually shown. The two live regions below are rendered unconditionally —
   * a region added to the page at the same moment as its content is not announced by most screen
   * readers — so the container is never `:empty` and cannot collapse on its own. A host that gives
   * this component padding keys off the attribute rather than guessing.
   */
  const showing =
    streams.status === 'failed' ||
    config.status === 'failed' ||
    observers.status === 'failed' ||
    modelFailed ||
    modelSkipped > 0 ||
    streamsEmpty ||
    config.status === 'none' ||
    configurationEmpty ||
    ambiguous ||
    loadSummary != null;

  return (
    <div className={className} data-has-notices={showing ? 'true' : 'false'}>
      {/* The three failure banners. Each announces assertively through `Banner`'s own `role="alert"`;
          this element only groups them. */}
      <div className="notice-region">
        {streams.status === 'failed' && (
          <Banner surface="on-solid" tone="error" title={streams.reason}>
            Observers still load and still show their regions — a loaded observer&rsquo;s camera
            simply cannot be named.{' '}
            <RetryButton label="Retry reading camera streams" onClick={() => onRetry('streams')} />
          </Banner>
        )}

        {config.status === 'failed' && (
          <Banner surface="on-solid" tone="error" title={config.reason}>
            No observers have been loaded, so the counts below are not this twin&rsquo;s.{' '}
            <RetryButton
              label="Retry reading the observer configuration"
              onClick={() => onRetry('config')}
            />
          </Banner>
        )}

        {observers.status === 'failed' && (
          <Banner surface="on-solid" tone="error" title={observers.reason}>
            No observers have been loaded. This is a failed read, not an empty twin.{' '}
            <RetryButton label="Retry reading observers" onClick={() => onRetry('observers')} />
          </Banner>
        )}

        {modelFailed && (
          <Banner surface="on-solid" tone="error" title="Could not read the running twin.">
            Observers loaded from the running twin cannot be checked for changes.
            {model.reason ? ` ${model.reason}` : ''}{' '}
            <RetryButton label="Retry reading the running twin" onClick={() => onRetry('model')} />
          </Banner>
        )}
      </div>

      {/*
        The empty states. Each shares NO sentence with the errored state above it — that is AC7, and
        it is the single most load-bearing property of this component.
      */}
      {streamsEmpty && (
        <Banner surface="on-solid" tone="info" title="This twin has no camera streams.">
          Observers still load and still show their regions; a loaded observer&rsquo;s camera cannot
          be named until a stream exists for it.
        </Banner>
      )}

      {config.status === 'none' && (
        <Banner surface="on-solid" tone="info" title="This twin has no observer configuration yet.">
          Nothing has been taken live for this twin, so there are no observers to load. Draw them
          here and press Go Live.
        </Banner>
      )}

      {modelSkipped > 0 && (
        <Banner surface="on-solid" tone="info" title="Some of the running twin was not compared.">
          {modelSkipped === 1
            ? '1 entity in the running twin has no identity and was not compared.'
            : `${modelSkipped} entities in the running twin have no identity and were not compared.`}
        </Banner>
      )}

      {configurationEmpty && (
        <Banner
          surface="on-solid"
          tone="info"
          title={"This twin's observer configuration is empty."}
        >
          Configuration #{config.status === 'ready' ? config.id : ''} exists and holds no observers.
        </Banner>
      )}

      {/* The advisory notices: what was chosen, and what could not be taken literally. Each
          announces politely through `Banner`'s own `role="status"`. */}
      <div className="notice-region">
        {ambiguous && config.status === 'ready' && (
          <Banner
            surface="on-solid"
            tone="warning"
            /* FR14/AC8's sentence, verbatim. More than one row may carry `is_active` — there is no
               unique index — so the stage names the one it chose rather than picking quietly. */
            title={`${config.activeIds.length} active configurations; using #${config.id}.`}
          >
            The highest id wins, so the same one is chosen every time. Ask an administrator to
            deactivate the others.
          </Banner>
        )}

        {noneActive && config.status === 'ready' && (
          <Banner
            surface="on-solid"
            tone="warning"
            title={`No configuration is marked active; showing the most recent, #${config.id}.`}
          >
            This twin may have been deactivated or rolled back. What you see is the newest
            configuration on record, not a running one.
          </Banner>
        )}

        {loadSummary &&
          (anythingToReport ? (
            <Banner surface="on-solid" tone="warning" title={loadSummary}>
              {unplaceable > 0 && (
                <p className="notice-detail">
                  {named.join(', ')}
                  {beyondNamed > 0 ? ` and ${beyondNamed} more` : ''}
                  {unplaceable === 1 ? ' names an entity' : ' name entities'} this plan does not
                  have, so there is nowhere to put {unplaceable === 1 ? 'it' : 'them'}.
                </p>
              )}
              {unreadable > 0 && (
                <p className="notice-detail">
                  {plural(unreadable, 'observer')} could not be placed because the region shape
                  stored against {unreadable === 1 ? 'it' : 'them'} could not be read.
                </p>
              )}
              {unrecognised > 0 && (
                <p className="notice-detail">
                  {plural(unrecognised, 'observer')} {unrecognised === 1 ? 'carries' : 'carry'} a
                  track side this app cannot represent (
                  {unrecognisedValues.map(trackSideLabel).join(', ')});{' '}
                  {unrecognised === 1 ? 'it loads' : 'they load'} as Torso.
                </p>
              )}
              {/*
                The REASON, not a re-derived sentence.

                ⚠ A measure lands in `unplaced` for four different causes — a member the plan does
                not have, a target it does not have, a floor it HAS but carries no identity for, and
                a member the SERVER could not resolve. `matchCompositesToPlan` writes a distinct
                sentence for each, naming the thing. One clause covering all four was wrong for
                three of them, and for the floor case it sent the reader looking for a floor that is
                on the screen in front of them — the exact failure that matcher's own comment says
                it exists to avoid.
              */}
              {namedComposites.map(u => (
                <p className="notice-detail" key={u.compositeId}>
                  {nameFor ? unplacedCompositeSentence(u, code => nameFor(code)) : u.reason}
                </p>
              ))}
              {beyondNamedComposites > 0 && (
                <p className="notice-detail">
                  {plural(beyondNamedComposites, 'other composite measure')} could not be placed
                  either; the full list is in the browser console.
                </p>
              )}
            </Banner>
          ) : (
            <p className="load-summary">{loadSummary}</p>
          ))}
      </div>
    </div>
  );
};

export default styled(LiveConfigurationNotices)`
  display: flex;
  flex-direction: column;
  gap: ${space[2]};

  /* A flex child set to display:none takes no gap, so with both regions empty this collapses to
     nothing but the host's own padding — which the host drops off data-has-notices. */
  .notice-region {
    display: flex;
    flex-direction: column;
    gap: ${space[2]};

    &:empty {
      display: none;
    }
  }

  .notice-detail {
    margin: ${space[1]} 0 0;
  }

  .load-summary {
    margin: 0;
    color: ${semantic.text.secondary};
    font-family: ${typeRole.caption1.fontFamily};
    font-size: ${typeRole.caption1.fontSize};
    line-height: ${typeRole.caption1.lineHeight};
  }
`;
